import { defaultState, TenantContractDataAction, TenantContractDataState } from "../reducers/TenantContractData";
import React, { Component } from "react";
import { connect } from "react-redux";
import { IStore } from "@my-dld/reducers/store";
import { dispatchX } from "@my-dld/utils/StoreUtils";
import { AttachmentInfo, TenancyContractResponse } from "@eres/ejari-api/Entity/tenant";
import { buildTenancyContract, buildTenancyContractFromRequest, buildTenancyTerminate, buildTenancyTerminateFromRequest } from "../TenantUtils";
import { EjariApi } from "@eres/ejari-api";
import { TenantDocumentLookup } from "@eres/ejari-api/Entity";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";

export interface ITenantContractContext extends TenantContractDataState {
	submitContract: ( ctx: ITenantContractContext ) => Promise<TenancyContractResponse>;
	renewContract: ( ctx: ITenantContractContext ) => Promise<TenancyContractResponse>;
	terminateContract: ( ctx: ITenantContractContext ) => Promise<TenancyContractResponse>;
	updateTenancyRequestAction: ( ctx: ITenantContractContext, userTypeId: string, status: number,
	                              remarks?: string ) => Promise<boolean>;
	updateTenancyTerminateRequestAction: ( ctx: ITenantContractContext, userTypeId: string, status: number,
	                                       remarks?: string ) => Promise<boolean>;
}

interface Props extends ITenantContractContext {

}

export const defaultContext: ITenantContractContext = {
	...defaultState,
	submitContract: () => Promise.reject( "Not ready" ),
	renewContract: () => Promise.reject( "Not ready" ),
	terminateContract: () => Promise.reject( "Not ready" ),
	updateTenancyRequestAction: () => Promise.reject( "Not ready" ),
	updateTenancyTerminateRequestAction: () => Promise.reject( "Not ready" ),
};

export const TenantContractContext = React.createContext( defaultContext );

interface State {

}

class TenantContractContextWrapper extends Component<Props, State> {
	constructor( props: Props ) {
		super( props );
		this.state = {};
	}

	render() {
		const { children, ...props } = this.props;
		return <TenantContractContext.Provider
			value={ {
				...props,
				submitContract: this.submitContract,
				renewContract: this.renewContract,
				terminateContract: this.terminateContract,
				updateTenancyRequestAction: this.updateTenancyRequestAction,
				updateTenancyTerminateRequestAction: this.updateTenancyTerminateRequestAction,
			} }>
			{ children }
		</TenantContractContext.Provider>;
	}

	submitContract = ( ctx: ITenantContractContext ) => {
		const request = buildTenancyContract( ctx );
		return this.uploadDocuments( request.TempContractId!, ctx.documentInfo!.Documents )
		.then( rep => {
			return EjariApi.submitTenancyContract( request ).then( resp => {
				return Promise.resolve( resp );
			} ).catch( e => {
				return Promise.reject( e.toString() );
			} );
		} ).catch( e => {
			return Promise.reject( e.toString() );
		} );
	};

	renewContract = ( ctx: ITenantContractContext ) => {
		const request = buildTenancyContract( ctx );
		return this.uploadDocuments( request.TempContractId!, ctx.documentInfo!.Documents )
		.then( rep => {
			return EjariApi.renewTenancyContract( request ).then( resp => {
				return Promise.resolve( resp );
			} ).catch( e => {
				return Promise.reject( e.toString() );
			} );
		} ).catch( e => {
			return Promise.reject( e.toString() );
		} );
	};

	terminateContract = ( ctx: ITenantContractContext ) => {
		const request = buildTenancyTerminate( ctx );
		return this.uploadDocuments( request.ContractId!, ctx.documentInfo!.Documents )
		.then( rep => {
			return EjariApi.terminateTenancyContract( request ).then( resp => {
				return Promise.resolve( resp );
			} ).catch( e => {
				return Promise.reject( e.toString() );
			} );
		} ).catch( e => {
			return Promise.reject( e.toString() );
		} );
	};

	updateTenancyRequestAction = ( ctx: ITenantContractContext, userTypeId: string, status: number, remarks?: string ) => {
		const request = buildTenancyContractFromRequest( ctx, userTypeId, status, remarks );
		return this.uploadDocuments( request.TenancyContractId!, ctx.documentInfo?.Documents ).then( rep => {
			return EjariApi.updateTenancyContract( request ).then( resp => {
				return Promise.resolve( true );
			} ).catch( e => {
				return Promise.reject( e.toString() );
			} );
		} ).catch( e => {
			return Promise.reject( e.toString() );
		} );
	};

	updateTenancyTerminateRequestAction = ( ctx: ITenantContractContext, userTypeId: string, status: number, remarks?: string ) => {
		const request = buildTenancyTerminateFromRequest( ctx, userTypeId, status, remarks );
		return this.uploadDocuments( request.ContractId!, ctx.documentInfo?.Documents ).then( rep => {
			return EjariApi.updateTerminationTenancyContract( request ).then( resp => {
				return Promise.resolve( true );
			} ).catch( e => {
				return Promise.reject( e.toString() );
			} );
		} ).catch( e => {
			return Promise.reject( e.toString() );
		} );
	};

	private uploadDocuments( contractId: number, documents?: TenantDocumentLookup[] ): Promise<AttachmentInfo[]> {
		const uploadTasks: Promise<AttachmentInfo>[] = [];
		if ( documents && documents.length > 0 ) {
			for ( const document of documents ) {
				const docs = document.Docs;
				const docId = document.Id;
				if ( docs && docs.length > 0 ) {
					for ( const doc of docs ) {
						if ( !isEmptyStringOrNull( doc.DocumentURL ) ) {
							uploadTasks.push( Promise.resolve( doc ) );
						} else if ( doc.LocalData ) {
							let extData = doc.LocalData.split( ";" )[ 0 ];
							let type = extData.includes( "image" ) ? "jpg" : "pdf";
							let encoded = doc.LocalData.replace( /^data:(.*;base64,)?/, "" );
							if ( ( encoded.length % 4 ) > 0 ) {
								encoded += "=".repeat( 4 - ( encoded.length % 4 ) );
							}
							uploadTasks.push( EjariApi.uploadTenancyContractDocument( encoded, type, docId, contractId, doc.FileName! )
								.then( resp => {
									doc.DocumentURL = resp.DocumentUrl;
									return Promise.resolve( doc );
								} ).catch( e => {
									return Promise.reject( e.toString() );
								} )
							);
						}
					}
				}
			}
		}
		return Promise.all( uploadTasks );
	}
}

const mapStateToProps = ( state: IStore, ownProps: Partial<ITenantContractContext> & { children?: any } ): ITenantContractContext => {
	return {
		...state.TenantContractData,
	} as Props;
};

export default connect( mapStateToProps )( TenantContractContextWrapper );

export function updateTenantContractContext( data: Partial<TenantContractDataState> ) {
	dispatchX( {
		type: TenantContractDataAction.TENANT_CONTRACT_UPDATE_STATE,
		payload: {
			...data
		}
	} );
}

export function resetTenantContractContext() {
	dispatchX( {
		type: TenantContractDataAction.TENANT_CONTRACT_CLEAR_DATA,
	} );
}
